/**
 * Individual search result row.
 * Shows icon, title, subtitle, and module badge.
 */
import React, { useMemo } from 'react';

import { getIcon } from '../../config/iconMap';
import { cn } from '../../../lib/utils';
import { Badge } from '../../../components/ui/badge';

interface GlobalSearchResultItemProps {
  title: string;
  subtitle: string | null;
  /** Icon name string (resolved via getIcon) — used for entity results from the API. */
  icon?: string | null;
  /** Pre-rendered icon node — used for page results from nav config. */
  iconNode?: React.ReactNode;
  moduleLabel?: string;
  /** Matched query lexemes to highlight in title/subtitle (Tier 3). */
  highlightTerms?: string[];
  isSelected: boolean;
  onClick: () => void;
  /** Result kind for E2E selectors: 'page' | 'tag' | 'entity' | 'suggestion'. */
  resultKind?: string;
  /** entity_type of the underlying record, exposed for E2E assertions. */
  dataEntityType?: string;
}

/** Escape a string for safe inclusion in a RegExp. */
function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Wrap occurrences of any matched term (and the rest of the word it prefixes)
 * in a themed <mark>, so users see *why* a result matched. Case-insensitive.
 * Returns the original text untouched when there is nothing to highlight.
 */
function highlightMatches(
  text: string,
  terms: string[] | undefined,
): React.ReactNode {
  const valid = (terms ?? []).filter((t) => t.length >= 2);
  if (valid.length === 0) return text;

  // Longest-first so "invoicing" is preferred over "invoice" at the same spot.
  const sorted = [...valid].sort((a, b) => b.length - a.length);
  // Match each term plus any trailing word chars (prefix terms highlight the
  // whole word, matching the search's prefix semantics).
  const pattern = new RegExp(
    `(${sorted.map(escapeRegExp).join('|')})\\w*`,
    'gi',
  );

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(
      <mark
        key={`hl-${key++}`}
        className="rounded-[2px] bg-primary/20 px-0.5 text-foreground"
      >
        {match[0]}
      </mark>,
    );
    lastIndex = match.index + match[0].length;
    // Guard against zero-length matches (shouldn't happen with \w* but safe).
    if (pattern.lastIndex === match.index) pattern.lastIndex++;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

export function GlobalSearchResultItem({
  title,
  subtitle,
  icon,
  iconNode,
  moduleLabel,
  highlightTerms,
  isSelected,
  onClick,
  resultKind,
  dataEntityType,
}: GlobalSearchResultItemProps) {
  const resolvedIcon = iconNode || (icon ? getIcon(icon) : null);

  const titleNode = useMemo(
    () => highlightMatches(title, highlightTerms),
    [title, highlightTerms],
  );
  const subtitleNode = useMemo(
    () => (subtitle ? highlightMatches(subtitle, highlightTerms) : null),
    [subtitle, highlightTerms],
  );

  return (
    <button
      type="button"
      data-testid="search-result-item"
      data-result-kind={resultKind}
      data-entity-type={dataEntityType}
      data-selected={isSelected || undefined}
      className={cn(
        'flex items-center gap-3 w-full px-3 py-2.5 text-left rounded-md min-h-[44px]',
        'hover:bg-accent transition-colors',
        isSelected && 'bg-accent',
      )}
      onClick={onClick}
    >
      {/* Icon */}
      <div className="flex-shrink-0 w-8 h-8 rounded-md bg-muted flex items-center justify-center">
        {resolvedIcon ? (
          <span className="[&>svg]:h-4 [&>svg]:w-4 text-muted-foreground">
            {resolvedIcon}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">?</span>
        )}
      </div>

      {/* Title + Subtitle — dir="auto" so mixed Hebrew/English entity names keep logical bidi order in RTL */}
      <div className="flex-1 min-w-0">
        <div dir="auto" className="text-sm font-medium truncate">{titleNode}</div>
        {subtitle && (
          <div dir="auto" className="text-xs text-muted-foreground truncate">{subtitleNode}</div>
        )}
      </div>

      {/* Module badge */}
      {moduleLabel && (
        <Badge variant="secondary" className="text-xs flex-shrink-0">
          {moduleLabel}
        </Badge>
      )}
    </button>
  );
}
